// ────────────────────────────────────────────────────────────────────────────
// What each Organisation-details field actually prints.
//
// One source of truth for both sides of the override feature: the renderer
// asks for the lines it should draw, and the inspector asks for the same lines
// to show as the placeholder in each edit box. That means what the operator
// sees greyed out in the builder is *exactly* what the document prints — if
// these ever diverged, the placeholder would be a lie.
//
// The override rule: an override replaces the whole printed text for that
// field, prefix included (so "VAT IE123" can become "VAT Reg. No. IE123"), and
// each line of the box is one printed line. Blank means "use the organisation
// record", so clearing the box restores the default rather than printing
// nothing — there is already a tick box for leaving a field out.
//
// Overrides live on the band, not on the organisation: they change how *this
// template* prints, never the record in Settings → Organisation.
// ────────────────────────────────────────────────────────────────────────────
import type { DocumentOrganisation } from './documentData';
import type { OrganisationField, OrganisationFieldOverrides } from './types';

/** Blank-ish values never print. */
function present(value: string | null | undefined): value is string {
  return Boolean(value && value.trim() && value.trim() !== '—');
}

/** The exact lines a field prints when no override is set. */
export function defaultOrganisationFieldLines(
  field: OrganisationField,
  org: DocumentOrganisation,
): string[] {
  switch (field) {
    case 'name':
      return present(org.name) ? [org.name] : [];
    case 'addressLines':
      return org.addressLines.filter(present);
    case 'taxNumber':
      return present(org.taxNumber) ? [`VAT ${org.taxNumber}`] : [];
    case 'registrationNumber':
      return present(org.registrationNumber) ? [`Reg. no. ${org.registrationNumber}`] : [];
    case 'phone':
      return present(org.phone) ? [`Tel ${org.phone}`] : [];
    case 'email':
      return present(org.email) ? [org.email] : [];
    case 'website':
      return present(org.website) ? [org.website] : [];
  }
}

/** Split an override into printed lines. Interior blanks are kept deliberately. */
function overrideLines(value: string): string[] {
  return value.trim().split(/\r?\n/);
}

/** The lines a field prints, override applied. Empty array = prints nothing. */
export function organisationFieldLines(
  field: OrganisationField,
  org: DocumentOrganisation,
  overrides: OrganisationFieldOverrides | undefined,
): string[] {
  const override = overrides?.[field];
  if (present(override)) return overrideLines(override);
  return defaultOrganisationFieldLines(field, org);
}

/** Placeholder text for the inspector — the default, exactly as it prints. */
export function defaultOrganisationFieldText(
  field: OrganisationField,
  org: DocumentOrganisation,
): string {
  return defaultOrganisationFieldLines(field, org).join('\n');
}

/** True when an override is set for this field. */
export function hasOverride(
  field: OrganisationField,
  overrides: OrganisationFieldOverrides | undefined,
): boolean {
  return present(overrides?.[field]);
}

/**
 * Set or clear one field's override. An empty value deletes the key rather
 * than storing `''`, so a cleared box round-trips as "no override" instead of
 * leaving an empty string in the saved layout.
 */
export function withOverride(
  overrides: OrganisationFieldOverrides | undefined,
  field: OrganisationField,
  value: string,
): OrganisationFieldOverrides {
  const next: OrganisationFieldOverrides = { ...overrides };
  if (value.trim()) next[field] = value;
  else delete next[field];
  return next;
}
